import { useCallback, useMemo } from 'react';
import type { FC } from 'react';

import { useIntl, defineMessage } from 'react-intl';

import { Map } from 'immutable';

import { quoteComposeCancel } from '@/mastodon/actions/compose_typed';
import { IconButton } from '@/mastodon/components/icon_button';
import { QuotedStatus } from '@/mastodon/components/status_quoted';
import Card from '@/mastodon/features/status/components/card';
import type { CardShape } from '@/mastodon/models/status';
import { useAppDispatch, useAppSelector } from '@/mastodon/store';
import CancelFillIcon from '@/material-icons/400-24px/cancel-fill.svg?react';

import { QuotePlaceholder } from './quote_placeholder';

const quoteCancelMessage = defineMessage({
  id: 'status.quote.cancel',
  defaultMessage: 'Cancel quote',
});

export const ComposeQuotedStatus: FC = () => {
  const quotedStatusId = useAppSelector(
    (state) => state.compose.get('quoted_status_id') as string | null,
  );

  const isFetchingLink = useAppSelector(
    (state) => !!state.compose.get('fetching_link'),
  );

  const isEditing = useAppSelector((state) => !!state.compose.get('id'));

  const quote = useMemo(
    () =>
      quotedStatusId
        ? Map<'state' | 'quoted_status', string>([
            ['state', 'accepted'],
            ['quoted_status', quotedStatusId],
          ])
        : null,
    [quotedStatusId],
  );

  const dispatch = useAppDispatch();
  const handleQuoteCancel = useCallback(() => {
    dispatch(quoteComposeCancel());
  }, [dispatch]);

  if (isFetchingLink && !quote) {
    return <QuotePlaceholder />;
  } else if (!quote) {
    return null;
  }

  return (
    <QuotedStatus
      quote={quote}
      contextType='composer'
      onQuoteCancel={!isEditing ? handleQuoteCancel : undefined}
    />
  );
};

// NewsItem equivalent of ComposeQuotedStatus above. NewsItem never federates
// and isn't a Status, so there's no status_quoted.tsx-style "QuotedStatus"
// to fetch/render here — just the same Card used everywhere else a NewsItem
// is shown, in the same bordered box a real quoted-post preview uses.
export const ComposeQuotedNewsItem: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const quotedNewsItemId = useAppSelector(
    (state) => state.compose.get('quoted_news_item_id') as string | null,
  );
  const newsItem = useAppSelector((state) =>
    quotedNewsItemId ? state.newsItems.items[quotedNewsItemId] : undefined,
  );

  const handleQuoteCancel = useCallback(() => {
    dispatch(quoteComposeCancel());
  }, [dispatch]);

  const card: CardShape | null = useMemo(
    () =>
      newsItem
        ? {
            url: newsItem.url,
            title: newsItem.title,
            description: newsItem.description,
            language: null,
            type: 'link',
            author_name: '',
            author_url: '',
            provider_name: newsItem.source_name,
            provider_url: '',
            html: '',
            width: 0,
            height: 0,
            image: newsItem.image_url,
            image_description: '',
            embed_url: '',
            blurhash: '',
            published_at: newsItem.published_at,
            authors: [],
          }
        : null,
    [newsItem],
  );

  if (!quotedNewsItemId || !card) {
    return null;
  }

  return (
    <div className='status__quote compose-form__quoted-news-item'>
      <IconButton
        onClick={handleQuoteCancel}
        className='status__quote-cancel'
        title={intl.formatMessage(quoteCancelMessage)}
        icon='cancel-fill'
        iconComponent={CancelFillIcon}
      />
      <Card card={card} />
    </div>
  );
};
